import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import BadgeStar from '../../components/shared/BadgeStar'
import RollBOT from '../../components/shared/RollBOT'
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'

export default function AthleteDetail({ athleteProfileId, onBack }) {
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [athlete, setAthlete] = useState(null)
  const [logs, setLogs] = useState([])
  const [badges, setBadges] = useState([])
  const [note, setNote] = useState('')
  const [notePrivate, setNotePrivate] = useState(false)
  const [savingNote, setSavingNote] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [notes, setNotes] = useState([])
  const [coachId, setCoachId] = useState(null)
  const [hillLogs, setHillLogs] = useState([])
  const [teamAverages, setTeamAverages] = useState(null)

  useEffect(() => {
    fetchAll()
  }, [athleteProfileId])

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) setCoachId(user.id)
    }
    init()
  }, [])

  useEffect(() => {
    if (!athlete) return
    const fetchNotes = async () => {
      const { data } = await supabase
        .from('coach_notes')
        .select('id, note, is_private, created_at, profiles!coach_notes_coach_id_fkey(full_name)')
        .eq('athlete_id', athlete.id)
        .order('created_at', { ascending: false })
      if (data) setNotes(data)
    }
    fetchNotes()
  }, [athlete])

  const fetchAll = async () => {
    setLoading(true)

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', athleteProfileId)
      .single()
    setProfile(profileData)

    const { data: athleteData } = await supabase
      .from('athletes')
      .select('*')
      .eq('profile_id', athleteProfileId)
      .single()
    setAthlete(athleteData)

    if (athleteData) {
      const { data: logData } = await supabase
        .from('session_logs')
        .select('*, sessions(session_date, day_type, mode)')
        .eq('athlete_id', athleteData.id)
        .order('created_at', { ascending: false })
        .limit(20)
      setLogs(logData || [])

      const { data: badgeData } = await supabase
        .from('athlete_badges')
        .select('*, badges(*)')
        .eq('athlete_id', athleteData.id)
      setBadges(badgeData || [])

      // Fetch hill repeat logs
      if (logData && logData.length > 0) {
        const logIds = logData.map(l => l.id)
        const { data: hillData } = await supabase
          .from('hill_repeat_logs')
          .select('*')
          .in('session_log_id', logIds)
        setHillLogs(hillData || [])
      }

      // Fetch rata-rata kategori untuk posisi relatif tim
      if (athleteData.category) {
        const { data: teamData } = await supabase
          .from('session_logs')
          .select('avg_speed, cadence, heart_rate, athletes!inner(category)')
          .eq('athletes.category', athleteData.category)
          .limit(100)
        if (teamData && teamData.length > 0) {
          const validSpeed = teamData.map(d => d.avg_speed).filter(Boolean)
          const validCadence = teamData.map(d => d.cadence).filter(Boolean)
          const validHr = teamData.map(d => d.heart_rate).filter(Boolean)
          setTeamAverages({
            avg_speed: validSpeed.length ? (validSpeed.reduce((a, b) => a + b, 0) / validSpeed.length).toFixed(1) : null,
            cadence: validCadence.length ? (validCadence.reduce((a, b) => a + b, 0) / validCadence.length).toFixed(1) : null,
            hr: validHr.length ? (validHr.reduce((a, b) => a + b, 0) / validHr.length).toFixed(1) : null,
          })
        }
      }
    }

    setLoading(false)
  }

  const chartData = [...logs]
    .reverse()
    .filter(l => l.rpe && l.avg_speed)
    .map(l => ({
      date: l.sessions?.session_date?.slice(5) || '-',
      RPE: l.rpe,
      Speed: parseFloat(l.avg_speed || 0),
      HR: l.heart_rate || null,
    }))

  const avgRPE = logs.length
    ? (logs.reduce((s, l) => s + (l.rpe || 0), 0) / logs.filter(l => l.rpe).length).toFixed(1)
    : '-'

  const avgCadence = logs.filter(l => l.cadence).length
    ? Math.round(logs.filter(l => l.cadence).reduce((s, l) => s + l.cadence, 0) / logs.filter(l => l.cadence).length)
    : '-'

  const streak = (() => {
    const dates = [...new Set(logs.map(l => l.sessions?.session_date).filter(Boolean))].sort().reverse()
    let count = 0
    let prev = null
    for (const d of dates) {
      if (!prev) { count = 1; prev = d; continue }
      const diff = (new Date(prev) - new Date(d)) / 86400000
      if (diff <= 7) { count++; prev = d } else break
    }
    return count
  })()

  const tooltipStyle = {
    background: '#1e293b', border: '1px solid #334155',
    borderRadius: '8px', color: '#f1f5f9', fontSize: '0.75rem'
  }

  const categoryColors = {
    'Challenge 5-6': '#34d399', 'Challenge 7-8': '#34d399',
    'Challenge 9-10': '#38bdf8', 'Challenge 11-12': '#38bdf8',
    'Challenge 13-14': '#a78bfa', 'Youth (15-16)': '#facc15',
    'Junior (17-18)': '#fb923c', 'Under-23': '#f87171', 'Elite': '#ef4444'
  }

  const catColor = categoryColors[athlete?.category] || '#94a3b8'

  const dayTypeLabel = {
    mtb_xc: 'MTB/XC', road_passing: 'Road Passing',
    indoor: 'Indoor', hill_repeat: 'Hill Repeat', long_ride: 'Long Ride'
  }

  const handleSaveNote = async () => {
  if (!note.trim()) return
  setSavingNote(true)
  try {
    const { error } = await supabase
      .from('coach_notes')
      .insert({
        athlete_id: athlete?.id,
        coach_id: null,
        note: note.trim(),
        is_private: notePrivate
      })
    if (error) throw error
    setNote('')
    setNotePrivate(false)
    await fetchAll()
  } catch (err) {
    alert('Gagal simpan: ' + err.message)
  }
  setSavingNote(false)
}

  if (loading) return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#38bdf8'
    }}>
      Loading...
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', overflowX: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '1rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: '1px solid #334155',
          color: '#94a3b8', padding: '0.4rem 0.75rem',
          borderRadius: '8px', cursor: 'pointer',
          fontSize: '0.85rem', marginRight: '1rem',
          flexShrink: 0
        }}>
          ← Kembali
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 'bold', fontSize: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile?.full_name}
          </div>
          <div style={{
            fontSize: '0.7rem', color: catColor,
            fontWeight: 'bold', textTransform: 'uppercase'
          }}>
            {athlete?.category || 'Kategori belum diset'}
          </div>
        </div>
      </div>

      {/* Stats ringkasan */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: '0.5rem', padding: '1rem 1rem 0',
        boxSizing: 'border-box'
      }}>
        {[
          { label: 'Total Sesi', value: logs.length, color: '#38bdf8', icon: '📊' },
          { label: 'Streak', value: `${streak}`, color: '#fb923c', icon: '🔥' },
          { label: 'Avg RPE', value: avgRPE, color: '#f472b6', icon: '💪' },
          { label: 'Avg Cadence', value: avgCadence ? `${avgCadence}` : '-', color: '#a78bfa', icon: '⚙️' },
        ].map(s => (
          <div key={s.label} style={{
            background: `${s.color}18`,
            border: `1px solid ${s.color}44`,
            borderRadius: '10px', padding: '0.6rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.9rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: s.color }}>
              {s.value}
            </div>
            <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #1e293b',
        padding: '0 0.5rem', marginTop: '1rem',
        overflowX: 'auto', WebkitOverflowScrolling: 'touch'
      }}>
        {[
          { id: 'overview', label: '📋 Overview' },
          { id: 'chart', label: '📈 Chart' },
          { id: 'history', label: '🕐 Riwayat' },
          { id: 'badges', label: '🏆 Badge' },
          { id: 'note', label: '📝 Catatan' },
          { id: 'rollbot', label: '✦ RollBOT' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '0.65rem 0.75rem',
            background: 'none', border: 'none',
            borderBottom: activeTab === tab.id ? '2px solid #34d399' : '2px solid transparent',
            color: activeTab === tab.id ? '#34d399' : '#94a3b8',
            cursor: 'pointer', fontSize: '0.75rem',
            fontWeight: activeTab === tab.id ? 'bold' : 'normal',
            whiteSpace: 'nowrap', marginBottom: '-1px'
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '1rem', overflowX: 'hidden', boxSizing: 'border-box' }}>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

            {/* Info fisik */}
            <div style={{
              background: '#1e293b', borderRadius: '12px', padding: '1rem'
            }}>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                DATA FISIK
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[
                  { label: 'Tinggi', value: athlete?.height_cm ? `${athlete.height_cm} cm` : '-' },
                  { label: 'Berat', value: athlete?.weight_kg ? `${athlete.weight_kg} kg` : '-' },
                  { label: 'HR Istirahat', value: athlete?.hr_rest ? `${athlete.hr_rest} bpm` : '-' },
                  { label: 'Pengalaman', value: athlete?.cycling_exp_years ? `${athlete.cycling_exp_years} tahun` : '-' },
                  { label: 'Cyclo Comp', value: athlete?.cyclo_comp || 'Belum Ada' },
                ].map(item => (
                  <div key={item.label} style={{
                    background: '#0f172a', borderRadius: '8px', padding: '0.5rem 0.75rem'
                  }}>
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{item.label}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#f1f5f9' }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sesi terakhir */}
            {logs[0] && (
              <div style={{
                background: '#1e293b', borderRadius: '12px', padding: '1rem'
              }}>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                  SESI TERAKHIR
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {dayTypeLabel[logs[0].sessions?.day_type] || '-'}
                      {logs[0].sessions?.mode ? ` — ${logs[0].sessions.mode}` : ''}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                      {logs[0].sessions?.session_date || '-'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.5rem', fontWeight: 'bold',
                    color: logs[0].rpe >= 8 ? '#ef4444' : logs[0].rpe >= 6 ? '#fb923c' : '#34d399'
                  }}>
                    RPE {logs[0].rpe}
                  </div>
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: '0.5rem', marginTop: '0.75rem'
                }}>
                  {[
                    { label: 'Speed', value: logs[0].avg_speed ? `${logs[0].avg_speed} km/j` : '-', color: '#facc15' },
                    { label: 'Cadence', value: logs[0].cadence ? `${logs[0].cadence} rpm` : '-', color: '#f472b6' },
                    { label: 'HR', value: logs[0].heart_rate ? `${logs[0].heart_rate} bpm` : '-', color: '#f87171' },
                    { label: 'Jarak', value: logs[0].distance ? `${logs[0].distance} km` : '-', color: '#4ade80' },
                  ].map(m => (
                    <div key={m.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: m.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.value}</div>
                      <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chart */}
        {activeTab === 'chart' && (
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
              RPE vs Avg Speed vs Heart Rate — 20 sesi terakhir
            </div>
            {chartData.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                Belum ada data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#fb923c" fontSize={11} domain={[0, 10]} />
                  <YAxis yAxisId="right" orientation="right" stroke="#38bdf8" fontSize={11} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Line yAxisId="left" type="monotone" dataKey="RPE"
                    stroke="#fb923c" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="Speed"
                    stroke="#38bdf8" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="HR"
                    stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }}
                    connectNulls={true} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {/* Riwayat sesi */}
        {activeTab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {logs.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                Belum ada riwayat sesi
              </div>
            ) : logs.map(log => (
              <div key={log.id} style={{
                background: '#1e293b', borderRadius: '10px',
                padding: '0.75rem 1rem',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {dayTypeLabel[log.sessions?.day_type] || '-'}
                    {log.sessions?.mode ? ` — ${log.sessions.mode}` : ''}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.2rem' }}>
                    {log.sessions?.session_date || '-'} •
                    Speed: {log.avg_speed || '-'} km/j •
                    Cadence: {log.cadence || '-'} rpm •
                    HR: {log.heart_rate ? `${log.heart_rate} bpm` : '-'}
                  </div>
                </div>
                <div style={{
                  fontSize: '1.1rem', fontWeight: 'bold',
                  color: log.rpe >= 8 ? '#ef4444' : log.rpe >= 6 ? '#fb923c' : '#34d399'
                }}>
                  RPE {log.rpe}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Badge */}
        {activeTab === 'badges' && (
          <div>
            <div style={{ color: '#facc15', fontWeight: 'bold', marginBottom: '1rem' }}>
              🏆 {badges.length} Badge Diraih
            </div>
            {badges.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                Belum ada badge
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                gap: '1rem', justifyItems: 'center'
              }}>
                {badges.map(ab => (
                  <BadgeStar
                    key={ab.id}
                    size={45}
                    color={ab.badges?.color || '#facc15'}
                    earned={true}
                    icon={ab.badges?.icon || '⭐'}
                    name={ab.badges?.name || ''}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Catatan coach */}
        {activeTab === 'note' && (
          <div>
            <div style={{ color: '#34d399', fontWeight: 'bold', marginBottom: '1rem' }}>
              📝 Tambah Catatan untuk {profile?.full_name?.split(' ')[0]}
            </div>

            {/* Toggle privasi */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              marginBottom: '0.75rem',
              background: '#1e293b', borderRadius: '10px', padding: '0.75rem'
            }}>
              <button
                onClick={() => setNotePrivate(!notePrivate)}
                style={{
                  padding: '0.4rem 0.85rem',
                  borderRadius: '999px',
                  border: notePrivate ? '2px solid #f87171' : '2px solid #34d399',
                  background: notePrivate ? '#f8717120' : '#34d39920',
                  color: notePrivate ? '#f87171' : '#34d399',
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'
                }}
              >
                {notePrivate ? '🔒 Privat' : '🌐 Publik'}
              </button>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                {notePrivate
                  ? 'Hanya coach & admin yang bisa lihat'
                  : 'Orang tua juga bisa lihat catatan ini'}
              </div>
            </div>

            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Tulis catatan, observasi, atau instruksi untuk atlet ini..."
              rows={5}
              style={{
                width: '100%', padding: '0.75rem',
                background: '#0f172a', border: '1px solid #334155',
                borderRadius: '10px', color: '#f1f5f9',
                fontSize: '0.9rem', resize: 'vertical',
                fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleSaveNote}
              disabled={savingNote || !note.trim()}
              style={{
                marginTop: '0.75rem',
                padding: '0.65rem 1.5rem',
                background: savingNote || !note.trim() ? '#334155' : '#34d399',
                color: savingNote || !note.trim() ? '#94a3b8' : '#0f172a',
                border: 'none', borderRadius: '8px',
                fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem'
              }}
            >
              {savingNote ? 'Menyimpan...' : '💾 Simpan Catatan'}
            </button>

            {/* Catatan sebelumnya */}
            {notes.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  CATATAN SEBELUMNYA
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {notes.map(n => (
                    <div key={n.id} style={{
                      background: '#1e293b', borderRadius: '8px', padding: '0.75rem',
                      borderLeft: `3px solid ${n.is_private ? '#f87171' : '#34d399'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{
                          fontSize: '0.65rem',
                          color: n.is_private ? '#f87171' : '#34d399'
                        }}>
                          {n.is_private ? '🔒 Privat' : '🌐 Publik'}
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#475569' }}>
                          {n.created_at?.slice(0, 10)}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: 1.6 }}>
                        {n.note}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* RollBOT */}
        {activeTab === 'rollbot' && (
          <div style={{ paddingTop: '0.5rem' }}>
            <RollBOT
              athlete={{ ...athlete, full_name: profile?.full_name }}
              sessions={logs.map(l => ({
                ...l,
                session_date: l.sessions?.session_date,
                day_type: l.sessions?.day_type,
                mode: l.sessions?.mode,
              }))}
              coachNotes={notes}
              hillLogs={hillLogs}
              badges={badges}
              teamAverages={teamAverages}
              role="coach"
            />
          </div>
        )}

      </div>
    </div>
  )
}

